// Client-side chart image export. Renders chart.js charts offscreen at the
// requested pixel size (native resolution, not an upscaled screenshot) and
// composites them over the chosen background. Maps are rendered offscreen too
// (tiles + markers), which is asynchronous, so use renderExportCanvas for a
// type-agnostic render. Uses the same builders as the on-screen charts, so
// images match the rendered results.

import { Chart } from 'chart.js';
import '../viz/chartSetup';
import { buildChart } from '../viz/chartData';
import { renderMapCanvas } from '../viz/mapCanvas';
import { paletteForBackground, resolveBackground } from '../viz/palette';

// Native export size: large enough for publication use, still fast to render.
export const EXPORT_SIZE = { width: 1600, height: 900 };

// Chart types that render to an image; tables are data output only.
export const IMAGE_CHART_TYPES = new Set(['bar', 'line', 'pie', 'radar', 'scatter', 'map']);

export function renderChartToCanvas({
  chartType,
  rows,
  fields,
  config,
  background,
  width = EXPORT_SIZE.width,
  height = EXPORT_SIZE.height
}) {
  const resolved = resolveBackground(background);
  const built = buildChart(chartType, rows, fields, config, paletteForBackground(resolved));
  if (!built) return null;

  const chartCanvas = document.createElement('canvas');
  chartCanvas.width = width;
  chartCanvas.height = height;
  const chart = new Chart(chartCanvas, {
    type: built.type,
    data: built.data,
    options: { ...built.options, responsive: false, animation: false, devicePixelRatio: 1 }
  });
  chart.update('none');

  const output = document.createElement('canvas');
  output.width = width;
  output.height = height;
  const ctx = output.getContext('2d');
  if (resolved !== 'transparent') {
    ctx.fillStyle = resolved;
    ctx.fillRect(0, 0, width, height);
  }
  ctx.drawImage(chartCanvas, 0, 0);
  chart.destroy();
  return output;
}

// Renders one exported item to a canvas whatever its chart type. Maps fetch
// their tiles, so this is asynchronous and rejects when they cannot load.
export async function renderExportCanvas({
  chartType,
  rows,
  fields,
  config,
  background,
  width = EXPORT_SIZE.width,
  height = EXPORT_SIZE.height
}) {
  const resolved = resolveBackground(background);
  if (chartType !== 'map') {
    return renderChartToCanvas({ chartType, rows, fields, config, background, width, height });
  }
  return renderMapCanvas({
    rows,
    fields,
    config: config || {},
    background: resolved,
    labelColor: paletteForBackground(resolved).onSurface,
    width,
    height
  });
}

// Stacks per-chart renders into one tall image with a title above each
// chart. `items` are { title, chartType, rows, fields, config }.
export async function renderStackedCharts(
  items,
  { background, width = EXPORT_SIZE.width, height = EXPORT_SIZE.height } = {}
) {
  const titleHeight = 44;
  const gap = 28;
  const padding = 32;

  const rendered = [];
  for (const item of items) {
    // Sequential: maps pull tiles, and parallel fetches would hammer the host.
    const canvas = await renderExportCanvas({
      ...item,
      background,
      width: width - padding * 2,
      height
    });
    if (canvas) rendered.push({ item, canvas });
  }
  if (rendered.length === 0) return null;

  const resolved = resolveBackground(background);
  const output = document.createElement('canvas');
  output.width = width;
  output.height =
    padding * 2 + rendered.length * (titleHeight + height) + (rendered.length - 1) * gap;
  const ctx = output.getContext('2d');
  if (resolved !== 'transparent') {
    ctx.fillStyle = resolved;
    ctx.fillRect(0, 0, width, output.height);
  }
  ctx.font = '600 28px system-ui, sans-serif';
  ctx.fillStyle = paletteForBackground(resolved).onSurface;
  let y = padding;
  for (const { item, canvas } of rendered) {
    ctx.fillText(item.title || '', padding, y + 28);
    ctx.drawImage(canvas, padding, y + titleHeight);
    y += titleHeight + height + gap;
  }
  return output;
}

// File-name-safe slug for download names.
export function slugify(text) {
  const slug = String(text || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'chart';
}
